import React, { useState, KeyboardEvent, ChangeEvent, useEffect } from 'react'
import { Card, Form, Button, Alert, Row, Col, Badge, Spinner } from 'react-bootstrap'
import { FaRegCopy, FaTimes } from 'react-icons/fa'
import { useAuth } from '../../../auth/auth-context'
import useFetch from '../../../_fetch'

const LinkScriptSettings: React.FC = () => {
    const { user } = useAuth()
    const { doFetch } = useFetch()

    const [allowedDomains, setAllowedDomains] = useState<string[]>([])
    const [allowedInput, setAllowedInput] = useState('')

    const [ignoredPages, setIgnoredPages] = useState<string[]>([])
    const [ignoredInput, setIgnoredInput] = useState('')

    const [copied, setCopied] = useState(false)
    const [saved, setSaved] = useState(false)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)

    const loadSettings = async () => {
        try {
            setError(null)
            setLoading(true)

            const res = await doFetch(`/byelink/settings`)
            if (!res.ok) throw new Error('Failed to load settings.')

            const data = await res.json()
            setAllowedDomains(data.allowedDomains || [])
            setIgnoredPages(data.ignoredPages || [])
        } catch (err: any) {
            setError(err.message)
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        if (user) loadSettings()
    }, [user])


    const handleAllowedInput = (e: ChangeEvent<HTMLInputElement>) =>
        setAllowedInput(e.target.value)
    const handleIgnoredInput = (e: ChangeEvent<HTMLInputElement>) =>
        setIgnoredInput(e.target.value)

    const handleAllowedKey = (e: KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter' || e.key === ',') {
            e.preventDefault()
            const value = allowedInput.trim()
            if (value && !allowedDomains.includes(value)) {
                setAllowedDomains([...allowedDomains, value])
            }
            setAllowedInput('')
        }
    }

    const handleIgnoredKey = (e: KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter' || e.key === ',') {
            e.preventDefault()
            const value = ignoredInput.trim()
            if (value && !ignoredPages.includes(value)) {
                setIgnoredPages([...ignoredPages, value])
            }
            setIgnoredInput('')
        }
    }

    const removeAllowed = (domain: string) =>
        setAllowedDomains(allowedDomains.filter(d => d !== domain))

    const removeIgnored = (page: string) =>
        setIgnoredPages(ignoredPages.filter(p => p !== page))


    const handleSave = async () => {
        try {
            setSaved(false)
            setError(null)

            const res = await doFetch(`/byelink/settings`, {allowedDomains, ignoredPages})

            if (!res.ok) throw new Error('Failed to save settings.')

            setSaved(true)
            setTimeout(() => setSaved(false), 2000)

        } catch (err: any) {
            setError(err.message)
        }
    }

    const handleCopy = () => {
        const script = `<script src="https://cdn.nordpar.com/api/${user?.username}/script.js"></script>`
        navigator.clipboard.writeText(script)
        setCopied(true)
        setTimeout(() => setCopied(false), 2000)
    }

    if (loading)
        return (
            <div className="d-flex justify-content-center mt-4">
                <Spinner animation="border" />
            </div>
        )

    return (
        <div className="w-100">
            {error && <Alert variant="danger">{error}</Alert>}

            <Row>
                <Col xs={12}>
                    <Card className="shadow-sm">
                        <Card.Header>
                            <h4 className="mb-0">Domain & Page Settings</h4>
                        </Card.Header>
                        <Card.Body>
                            <Row>
                                <Col xs={12} md={6} className="mb-4 mb-md-0">
                                    <h5>Allowed Domains</h5>
                                    <p className="text-muted">
                                        Domains where your script is allowed to run.
                                    </p>
                                    <Form.Group className="mb-3">
                                        <Form.Control
                                            type="text"
                                            placeholder="Add domain and press Enter"
                                            value={allowedInput}
                                            onChange={handleAllowedInput}
                                            onKeyDown={handleAllowedKey}
                                        />
                                    </Form.Group>
                                    <div className="d-flex flex-wrap gap-2">
                                        {allowedDomains.map((domain, idx) => (
                                            <Badge key={idx} bg="success" className="d-flex align-items-center px-2 py-1">
                                                {domain}
                                                <FaTimes
                                                    className="ms-2"
                                                    style={{ cursor: 'pointer' }}
                                                    onClick={() => removeAllowed(domain)}
                                                />
                                            </Badge>
                                        ))}
                                    </div>
                                </Col>

                                <Col xs={12} md={6}>
                                    <h5>Ignored Pages</h5>
                                    <p className="text-muted">
                                        URLs or domains to ignore.
                                    </p>
                                    <Form.Group className="mb-3">
                                        <Form.Control
                                            type="text"
                                            placeholder="Add URL or domain and press Enter"
                                            value={ignoredInput}
                                            onChange={handleIgnoredInput}
                                            onKeyDown={handleIgnoredKey}
                                        />
                                    </Form.Group>
                                    <div className="d-flex flex-wrap gap-2">
                                        {ignoredPages.map((page, idx) => (
                                            <Badge key={idx} bg="danger" className="d-flex align-items-center px-2 py-1">
                                                {page}
                                                <FaTimes
                                                    className="ms-2"
                                                    style={{ cursor: 'pointer' }}
                                                    onClick={() => removeIgnored(page)}
                                                />
                                            </Badge>
                                        ))}
                                    </div>
                                </Col>
                            </Row>

                            <div className="mt-5">
                                <Button variant="success" onClick={handleSave}>Save Settings</Button>
                                {saved && <Alert variant="success" className="mt-3">Settings saved!</Alert>}
                            </div>
                        </Card.Body>
                    </Card>
                </Col>

                <Col xs={12} md={6}>
                    <Card className="shadow-sm mt-4">
                        <Card.Body>
                            <h4 className="mb-3">Script</h4>
                            <p className="text-muted mb-3"> Copy this script to your website to automatically replace links with your affiliate links. Place it at the end of the <code>&lt;body&gt;</code> section. </p>
                            <Form.Group className="mb-3">
                                <Form.Control
                                    as="textarea"
                                    rows={1}
                                    readOnly
                                    value={`<script src="https://cdn.nordpar.com/api/${user?.username}/script.js"></script>`}
                                    className="bg-light monospace"
                                />
                            </Form.Group>
                            <Button variant="primary" onClick={handleCopy}>
                                <FaRegCopy className="me-2" /> Copy to clipboard
                            </Button>
                            {copied && <Alert variant="success" className="mt-2">Copied to clipboard!</Alert>}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </div>
    )
}

export default LinkScriptSettings
